import { acceptHMRUpdate, defineStore } from 'pinia'
import { ref, shallowRef } from 'vue'

import api from '@/api'
import { bookingsApi } from '@/api/client'
import type { GetLocations200ResponseInner } from '@/openapi'

export type LocationInfo = {
  locationId: string
  schedules: Record<
    string,
    {
      id: string
      locationId: string
      day: string
      start: string
      end: string
    }[]
  >
  brandInfo: {
    id: string
    locationId: string
    brandId: string
    enabled: boolean
    name: string
    coverImageId: string
    colorLogoImageId: string
    faviconImageId: string | null
    accentColor: string
    secondaryColor: string | null
    topbarColor: string | null
    backgroundImageId: string | null
    backgroundColor: string | null
    squareLogo: string | null
    position: number
  }
  timezone: string
  locationAddress: string
  locationPhoneNumber: string
  zones: {
    zone: string
  }[]
  maxSeats: {
    terraza: number
    sala: number
  }
  scripts: any
}

export const useAuthStore = defineStore('auth', () => {
  const organizationId = ref<string | null>(null)
  const locationId = ref<string | null>(null)
  const schedules = ref<
    Record<
      string,
      {
        id: string
        day: string
        start: string
        end: string
        locationId: string
      }[]
    >
  >({})
  const brandInfo = ref<Partial<LocationInfo['brandInfo']>>({})
  const timezone = ref<string>('Europe/Madrid')
  const locationAddress = ref<string | null>(null)
  const locationPhoneNumber = ref<string | null>(null)
  const zones = ref<string[]>([])
  const maxSeats = ref<Record<string, number>>({})
  const scripts = ref<Record<string, any>>({})

  const organizationLocations = shallowRef<
    GetLocations200ResponseInner[] | null
  >(null)

  function setLocationId(id: string) {
    locationId.value = id
  }

  function setOrganizationId(id: string) {
    organizationId.value = id
  }

  async function getOrganizationLocations() {
    if (!organizationId.value) return
    organizationLocations.value = await bookingsApi.getLocations()
  }

  async function getLocationInfo() {
    if (!locationId.value) return
    const response = await api.get<LocationInfo>('/bookings/location')
    setLocationInfo(response.data)
  }

  function setLocationInfo({
    schedules: newSchedules,
    brandInfo: newBrandInfo,
    timezone: newTimezone,
    locationAddress: newLocationAddress,
    locationPhoneNumber: newLocationPhoneNumber,
    zones: newZones,
    maxSeats: newMaxSeats,
    scripts: newScripts
  }: LocationInfo) {
    schedules.value = newSchedules
    brandInfo.value = newBrandInfo
    timezone.value = newTimezone
    locationAddress.value = newLocationAddress
    locationPhoneNumber.value = newLocationPhoneNumber
    zones.value = newZones?.map(zone => zone.zone) || []
    maxSeats.value = newMaxSeats
    scripts.value = newScripts
  }

  return {
    locationId,
    schedules,
    brandInfo,
    timezone,
    locationAddress,
    locationPhoneNumber,
    zones,
    maxSeats,
    scripts,
    organizationId,
    organizationLocations,
    setLocationId,
    getLocationInfo,
    setOrganizationId,
    getOrganizationLocations
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
